import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { DataService, PrivacySection } from 'src/app/services/data.service';

interface PolicyView {
  effectiveDate: string;
  intro: string;
  sections: { icon: string; title: string; body: string }[];
  contactTitle: string;
  contactBody: string;
  contactEmail: string;
  lastUpdated: string;
}

/**
 * Public privacy policy page. Reads config/privacyPolicy from Firestore — the
 * SAME source the mobile app renders and the admin "Confidentialité" tab edits —
 * so editing the policy updates web and mobile together. Every field falls back,
 * field by field, to the bundled default (DEFAULT_FR / DEFAULT_EN), mirroring the
 * Flutter PrivacyPolicyService behaviour. Language follows the build's LOCALE_ID.
 */
@Component({
  selector: 'app-privacy-policy',
  templateUrl: './privacy-policy.component.html',
  styleUrls: ['./privacy-policy.component.scss'],
})
export class PrivacyPolicyComponent implements OnInit, OnDestroy {
  private readonly isFr: boolean;
  view: PolicyView;
  private sub: Subscription;

  constructor(
    private data: DataService,
    @Inject(LOCALE_ID) locale: string,
  ) {
    this.isFr = (locale || 'en-US').toLowerCase().startsWith('fr');
    this.view = this.buildFromDefaults();
  }

  ngOnInit(): void {
    this.goToTop();
    this.sub = this.data.getPrivacyPolicy().subscribe((cfg: any) => {
      this.view = this.merge(cfg || {});
    }, () => { /* offline / denied → keep the defaults already shown */ });
  }

  ngOnDestroy(): void {
    if (this.sub) { this.sub.unsubscribe(); }
  }

  goToTop(): void {
    const el = document.getElementById('goToTop');
    if (el) { el.scrollIntoView({ behavior: 'smooth' }); }
  }

  /** Icon key (from the admin editor / mobile) → emoji for the web. */
  iconEmoji(key: string): string {
    const map: { [k: string]: string } = {
      handshake: '🤝', folder_shared: '🗂️', tune: '🎛️', share: '🔗',
      lock: '🔒', schedule: '⏳', gavel: '⚖️', insights: '📊',
      child_care: '🧒', public: '🌍', update: '🔄', shield: '🛡️',
      payments: '💳', badge: '🪪', support_agent: '🎧', article: '📄',
    };
    return map[key] || '🛡️';
  }

  // ── Merge Firestore over the defaults, field by field ──────────────────────
  private pick(remote: any, fr: string, en: string, dflt: string): string {
    const v = this.isFr ? remote?.[fr] : remote?.[en];
    return (typeof v === 'string' && v.trim()) ? v : dflt;
  }

  private merge(cfg: any): PolicyView {
    const d = this.defaults();
    const remoteSections: PrivacySection[] = Array.isArray(cfg.sections) ? cfg.sections : [];
    const sections = remoteSections.length
      ? remoteSections.map(s => ({
          icon: s.icon || 'shield',
          title: (this.isFr ? s.titleFr : s.titleEn) || '',
          body: (this.isFr ? s.bodyFr : s.bodyEn) || '',
        }))
      : d.sections;

    return {
      effectiveDate: this.pick(cfg, 'effectiveDateFr', 'effectiveDateEn', d.effectiveDate),
      intro: this.pick(cfg, 'introFr', 'introEn', d.intro),
      sections,
      contactTitle: this.pick(cfg, 'contactTitleFr', 'contactTitleEn', d.contactTitle),
      contactBody: this.pick(cfg, 'contactBodyFr', 'contactBodyEn', d.contactBody),
      contactEmail: (typeof cfg.contactEmail === 'string' && cfg.contactEmail.trim())
        ? cfg.contactEmail : d.contactEmail,
      lastUpdated: this.pick(cfg, 'lastUpdatedFr', 'lastUpdatedEn', d.lastUpdated),
    };
  }

  private buildFromDefaults(): PolicyView { return this.defaults(); }

  private defaults(): PolicyView {
    return this.isFr ? DEFAULT_FR : DEFAULT_EN;
  }
}

// ── Bundled default content (mirrors the Flutter EN/FR strings) ───────────────

const DEFAULT_EN: PolicyView = {
  effectiveDate: 'Effective date: June 14, 2026',
  intro: 'Your privacy matters to us. This policy explains what information Early Transfert '
    + 'collects, how we use and protect it, and the choices you have when you send money and post '
    + 'or accept exchange opportunities.',
  sections: [
    { icon: 'handshake', title: 'Our commitment',
      body: 'Early Transfert lets you transfer money between Canada and Cameroon and post or accept '
        + 'exchange opportunities with other users. We only collect what we need to run these services '
        + 'safely, comply with the law, and protect you against fraud — nothing more.' },
    { icon: 'folder_shared', title: 'Information we collect',
      body: '• Account & profile: name, email, phone number, country and address.\n'
        + '• Identity (KYC): the documents and details you provide to verify your identity, as required '
        + 'by financial regulations.\n'
        + '• Financial & wallet: your wallet balances and the deposit details you add (Interac email in '
        + 'Canada, Mobile Money number in Cameroon).\n'
        + '• Transactions: amounts, exchange rates, counterparties and status of your transfers.\n'
        + '• Posts & offers: the exchange opportunities you publish, the offers you make, likes and messages.\n'
        + '• Device & usage: app version, device identifiers and basic usage needed for security and notifications.' },
    { icon: 'tune', title: 'How we use your information',
      body: 'We use your information to process transfers and deposits, match posts with offers, verify '
        + 'your identity (KYC), detect and prevent fraud and money laundering, provide customer support, '
        + 'send you transaction and security notifications, and meet our legal and regulatory obligations.' },
    { icon: 'share', title: 'How we share information',
      body: 'We share information only when needed to operate the service: with payment partners (Interac, '
        + 'MTN MoMo, Orange Money) to move funds, with regulators and authorities when legally required '
        + '(anti-money-laundering, fraud), and with trusted service providers who help us run the app under '
        + 'strict confidentiality. We never sell your personal data.' },
    { icon: 'lock', title: 'How we protect your data',
      body: 'Your data is encrypted in transit and stored on secured infrastructure. Access is restricted '
        + 'to authorized staff on a need-to-know basis. While no system is 100% secure, we continuously '
        + 'work to safeguard your information.' },
    { icon: 'schedule', title: 'Data retention',
      body: 'We keep transaction records for as long as required by financial and anti-money-laundering '
        + 'laws. Posts you delete go to a trash where they remain restorable for 30 days, after which they '
        + 'are permanently deleted. You can ask us to delete data that we are not legally required to keep.' },
    { icon: 'gavel', title: 'Your rights',
      body: 'You can access and update your profile in the app at any time, request a copy or deletion of '
        + 'your personal data, withdraw a consent you gave, or lodge a complaint. Contact us using the '
        + 'details below and we will respond within a reasonable time.' },
    { icon: 'insights', title: 'Analytics & cookies',
      body: 'We use limited analytics and diagnostics to keep the app reliable and improve your experience. '
        + 'We do not use this information to sell your data or for intrusive advertising.' },
    { icon: 'child_care', title: 'Children',
      body: 'Early Transfert is intended for users aged 18 and over. We do not knowingly collect personal '
        + 'information from minors.' },
    { icon: 'public', title: 'International transfers',
      body: 'Because we operate between Canada and Cameroon, your information may be processed in both '
        + 'countries. We apply the same protections wherever your data is handled.' },
    { icon: 'update', title: 'Changes to this policy',
      body: 'We may update this policy from time to time. When we make significant changes, we will notify '
        + 'you in the app. The "last updated" date below always reflects the current version.' },
  ],
  contactTitle: 'Contact us',
  contactBody: 'For any question about this policy or your personal data, contact Vofack Consultation Inc. '
    + 'at privacy@earlytransfert.com.',
  contactEmail: 'privacy@earlytransfert.com',
  lastUpdated: 'Last updated: June 14, 2026',
};

const DEFAULT_FR: PolicyView = {
  effectiveDate: "Date d'effet : 14 juin 2026",
  intro: 'Votre vie privée compte pour nous. Cette politique explique quelles informations Early '
    + 'Transfert collecte, comment nous les utilisons et les protégeons, et les choix dont vous disposez '
    + "lorsque vous envoyez de l'argent et publiez ou acceptez des opportunités d'échange.",
  sections: [
    { icon: 'handshake', title: 'Notre engagement',
      body: "Early Transfert vous permet de transférer de l'argent entre le Canada et le Cameroun et de "
        + "publier ou accepter des opportunités d'échange avec d'autres utilisateurs. Nous ne collectons "
        + 'que ce qui est nécessaire pour faire fonctionner ces services en toute sécurité, respecter la '
        + 'loi et vous protéger contre la fraude — rien de plus.' },
    { icon: 'folder_shared', title: 'Informations que nous collectons',
      body: '• Compte & profil : nom, e-mail, numéro de téléphone, pays et adresse.\n'
        + "• Identité (KYC) : les documents et informations que vous fournissez pour vérifier votre "
        + "identité, comme l'exige la réglementation financière.\n"
        + '• Finances & wallet : vos soldes et les informations de dépôt que vous ajoutez (e-mail Interac '
        + 'au Canada, numéro Mobile Money au Cameroun).\n'
        + '• Transactions : montants, taux de change, contreparties et statut de vos transferts.\n'
        + "• Annonces & offres : les opportunités que vous publiez, les offres que vous faites, les "
        + "mentions « j'aime » et les messages.\n"
        + "• Appareil & usage : version de l'app, identifiants d'appareil et données d'usage de base "
        + 'nécessaires à la sécurité et aux notifications.' },
    { icon: 'tune', title: 'Comment nous utilisons vos informations',
      body: 'Nous utilisons vos informations pour traiter les transferts et dépôts, mettre en relation les '
        + 'annonces et les offres, vérifier votre identité (KYC), détecter et prévenir la fraude et le '
        + 'blanchiment d\'argent, fournir le support client, vous envoyer des notifications de transaction '
        + 'et de sécurité, et respecter nos obligations légales et réglementaires.' },
    { icon: 'share', title: 'Partage des informations',
      body: "Nous partageons des informations uniquement lorsque c'est nécessaire au service : avec les "
        + 'partenaires de paiement (Interac, MTN MoMo, Orange Money) pour déplacer les fonds, avec les '
        + "régulateurs et autorités lorsque la loi l'exige (lutte anti-blanchiment, fraude), et avec des "
        + 'prestataires de confiance qui nous aident à faire fonctionner l\'app sous stricte '
        + 'confidentialité. Nous ne vendons jamais vos données personnelles.' },
    { icon: 'lock', title: 'Comment nous protégeons vos données',
      body: 'Vos données sont chiffrées en transit et stockées sur une infrastructure sécurisée. L\'accès '
        + 'est limité au personnel autorisé selon le strict besoin d\'en connaître. Aucun système n\'est '
        + 'sûr à 100 %, mais nous œuvrons en permanence à protéger vos informations.' },
    { icon: 'schedule', title: 'Conservation des données',
      body: "Nous conservons les enregistrements de transactions aussi longtemps que l'exigent les lois "
        + 'financières et anti-blanchiment. Les annonces que vous supprimez vont dans une corbeille où '
        + 'elles restent restaurables pendant 30 jours, après quoi elles sont supprimées définitivement. '
        + 'Vous pouvez nous demander de supprimer les données que nous ne sommes pas légalement tenus de '
        + 'conserver.' },
    { icon: 'gavel', title: 'Vos droits',
      body: 'Vous pouvez consulter et mettre à jour votre profil dans l\'app à tout moment, demander une '
        + 'copie ou la suppression de vos données personnelles, retirer un consentement donné, ou déposer '
        + 'une réclamation. Contactez-nous via les coordonnées ci-dessous et nous répondrons dans un délai '
        + 'raisonnable.' },
    { icon: 'insights', title: 'Analytique & cookies',
      body: 'Nous utilisons une analytique et des diagnostics limités pour garder l\'app fiable et '
        + 'améliorer votre expérience. Nous n\'utilisons pas ces informations pour vendre vos données ni '
        + 'pour de la publicité intrusive.' },
    { icon: 'child_care', title: 'Mineurs',
      body: 'Early Transfert est destiné aux utilisateurs de 18 ans et plus. Nous ne collectons pas '
        + 'sciemment d\'informations personnelles de mineurs.' },
    { icon: 'public', title: 'Transferts internationaux',
      body: 'Comme nous opérons entre le Canada et le Cameroun, vos informations peuvent être traitées '
        + 'dans les deux pays. Nous appliquons les mêmes protections partout où vos données sont traitées.' },
    { icon: 'update', title: 'Modifications de cette politique',
      body: 'Nous pouvons mettre à jour cette politique de temps à autre. En cas de changement important, '
        + 'nous vous en informerons dans l\'app. La date de « dernière mise à jour » ci-dessous reflète '
        + 'toujours la version en vigueur.' },
  ],
  contactTitle: 'Nous contacter',
  contactBody: 'Pour toute question sur cette politique ou vos données personnelles, contactez Vofack '
    + 'Consultation Inc. à privacy@earlytransfert.com.',
  contactEmail: 'privacy@earlytransfert.com',
  lastUpdated: 'Dernière mise à jour : 14 juin 2026',
};
